import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ROLE_LABEL, ROLE_BADGE } from "../lib/permissions";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, Pencil, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const ROLES = ["president","vice_president","social_chair","rush_chair","chaplain","philanthropy_chair","scholarship_chair","treasurer","alumni_chair","marshal","risk_chair","recorder","member","candidate","alumni"];

export default function RosterView({ users, me, canManage, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "member", password: "Welcome123!" });
  const [credTarget, setCredTarget] = useState(null);
  const [credForm, setCredForm] = useState({ email: "", password: "" });
  const { impersonate } = useAuth();
  const isAdmin = me.role === "admin";

  const loginAs = async (u) => {
    try {
      const { data } = await api.post("/auth/impersonate", { user_id: u.id });
      impersonate(data.token, data.user);
      toast.success(`Viewing as ${u.name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Impersonation failed"); }
  };

  const saveCreds = async () => {
    if (!credForm.email && !credForm.password) { toast.error("Enter a new email or password"); return; }
    try {
      await api.patch(`/users/${credTarget.id}/credentials`, {
        email: credForm.email || undefined,
        password: credForm.password || undefined,
      });
      toast.success("Credentials updated");
      setCredTarget(null);
      setCredForm({ email: "", password: "" });
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const add = async () => {
    try {
      await api.post("/users", form);
      toast.success("Member added");
      setOpen(false);
      setForm({ name: "", email: "", role: "member", password: "Welcome123!" });
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/users/${id}`, { role });
      toast.success("Role updated");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const togglePosition = async (u, role, checked) => {
    const extras = u.roles || [];
    const next = checked ? [...extras, role] : extras.filter(r => r !== role);
    try {
      await api.patch(`/users/${u.id}`, { roles: next });
      toast.success("Positions updated");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name} from the roster?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Member removed");
      onRefresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[var(--sn-gold)]">Chapter Roster</h2>
          <p className="text-slate-500 text-sm">{users.length} brothers · {canManage ? "You can add, remove, and re-rank members." : "View only."}</p>
        </div>
        {canManage && (
          <Button data-testid="add-member-button" onClick={() => setOpen(true)}
            className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
            <UserPlus className="w-4 h-4" /> Add Member
          </Button>
        )}
      </div>

      <Card className="glass-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500 border-b border-white/5 bg-slate-900/40">
          <div className="col-span-4">Brother</div>
          <div className="col-span-4">Email</div>
          <div className="col-span-3">Rank</div>
          <div className="col-span-1"></div>
        </div>
        {users.map(u => (
          <div key={u.id} className="grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-white/5 hover:bg-white/5" data-testid={`roster-row-${u.id}`}>
            <div className="col-span-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center text-amber-400 font-semibold">
                {u.name.charAt(0)}
              </div>
              <div>
                <div className="text-slate-200 text-sm">{u.name}</div>
                {u.email === me.email && <div className="text-[10px] text-amber-400">You</div>}
              </div>
            </div>
            <div className="col-span-4 text-slate-400 text-sm truncate">{u.email}</div>
            <div className="col-span-3 flex items-center gap-1.5 flex-wrap">
              {canManage && u.role !== "admin" ? (
                <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                  <SelectTrigger data-testid={`role-select-${u.id}`} className="bg-slate-900 border-slate-700 h-9 text-xs w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Badge className={`${ROLE_BADGE[u.role]} border`}>
                  {u.role === "admin" && <Shield className="w-3 h-3 mr-1 inline" />}
                  {ROLE_LABEL[u.role]}
                </Badge>
              )}
              {(u.roles || []).map(r => (
                <Badge key={r} data-testid={`extra-role-${u.id}-${r}`} className={`${ROLE_BADGE[r]} border text-[10px]`}>
                  {ROLE_LABEL[r]}
                </Badge>
              ))}
              {canManage && u.role !== "admin" && (
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid={`positions-button-${u.id}`}
                      className="text-[10px] px-2 py-1 rounded-full border border-dashed border-slate-600 text-slate-400 hover:border-amber-500/50 hover:text-amber-400 transition">
                      + Position
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-56 p-2 bg-slate-900 border-slate-700 max-h-64 overflow-y-auto" data-testid={`positions-menu-${u.id}`}>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 px-2 pb-1">Additional positions</div>
                    {ROLES.filter(r => r !== u.role).map(r => (
                      <label key={r} data-testid={`position-option-${u.id}-${r}`}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5 cursor-pointer text-sm text-slate-300">
                        <Checkbox
                          checked={(u.roles || []).includes(r)}
                          onCheckedChange={(c) => togglePosition(u, r, !!c)}
                          className="border-slate-600 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                        />
                        {ROLE_LABEL[r]}
                      </label>
                    ))}
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              {isAdmin && u.role !== "admin" && u.email !== me.email && (
                <Button data-testid={`impersonate-${u.id}`} variant="ghost" size="icon"
                        title="Log in as this member"
                        onClick={() => loginAs(u)}
                        className="text-slate-500 hover:text-[var(--sn-gold)]">
                  <Eye className="w-4 h-4" />
                </Button>
              )}
              {canManage && (
                <Button data-testid={`edit-creds-${u.id}`} variant="ghost" size="icon"
                        onClick={() => { setCredTarget(u); setCredForm({ email: "", password: "" }); }}
                        className="text-slate-500 hover:text-[var(--sn-gold)]">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canManage && u.role !== "admin" && u.email !== me.email && (
                <Button data-testid={`remove-member-button-${u.id}`} variant="ghost" size="icon"
                        onClick={() => remove(u.id, u.name)}
                        className="text-slate-500 hover:text-rose-400">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </Card>

      <Dialog open={!!credTarget} onOpenChange={(o) => !o && setCredTarget(null)}>
        <DialogContent className="bg-[#141414] border-[#866D4B]/40" data-testid="edit-creds-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[var(--sn-gold)]">Edit Credentials</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-neutral-400">{credTarget?.name} · <span className="font-mono text-xs">{credTarget?.email}</span></p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>New email</Label>
              <Input data-testid="creds-new-email" type="email" value={credForm.email}
                onChange={(e) => setCredForm({ ...credForm, email: e.target.value })}
                placeholder={credTarget?.email} className="bg-neutral-900 border-[#866D4B]/40" /></div>
            <div className="space-y-1.5"><Label>New password (min 6)</Label>
              <Input data-testid="creds-new-password" type="text" value={credForm.password}
                onChange={(e) => setCredForm({ ...credForm, password: e.target.value })}
                placeholder="Leave blank to keep current" className="bg-neutral-900 border-[#866D4B]/40" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredTarget(null)} className="border-neutral-700 text-neutral-300">Cancel</Button>
            <Button data-testid="creds-save-button" onClick={saveCreds} className="bg-[var(--sn-gold)] text-black hover:brightness-95 font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800" data-testid="add-member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-300">Add Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Full Name</Label>
              <Input data-testid="add-member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input data-testid="add-member-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Initial Password</Label>
              <Input data-testid="add-member-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Rank</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="add-member-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {ROLES.map(r => <SelectItem key={r} value={r}>{ROLE_LABEL[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 text-slate-300">Cancel</Button>
            <Button data-testid="add-member-submit" onClick={add} className="bg-[var(--sn-gold)] text-black hover:brightness-95">Add Brother</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
